import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LocaleSwitcher from './LocaleSwitcher';
import useLocale, { type Locale } from '@/hooks/useLocale';

const LINE_URL = 'https://lin.ee/X7Hcpf4';

interface SubItem {
  label: string;
  path: string;
}

interface NavLink {
  label: string;
  path: string;
  sub: SubItem[];
}

// ── 各語系導覽結構 ─────────────────────────────────────────────

const ZH_NAV: NavLink[] = [
  {
    label: '首頁',
    path: '/',
    sub: [
      { label: '核心理念', path: '/home/core' },
      { label: '康永優勢', path: '/home/advantages' },
      { label: '服務經歷', path: '/home/experience' },
      { label: '熱銷配方', path: '/home/hot-formulas' },
    ],
  },
  {
    label: '關於康永',
    path: '/about',
    sub: [
      { label: '核心優勢', path: '/about/core-advantages' },
      { label: '專業生產', path: '/about/production' },
      { label: '成功案例', path: '/about/cases' },
      { label: '法規風險', path: '/about/regulation' },
    ],
  },
  {
    label: '合作相關',
    path: '/partnership',
    sub: [
      { label: '合作流程', path: '/cooperation/process' },
      { label: '其他服務', path: '/cooperation/services' },
      { label: '常見問題', path: '/cooperation/faq' },
      { label: '立即洽詢', path: '/cooperation/inquiry' },
    ],
  },
  {
    label: '代工服務',
    path: '/services',
    sub: [
      { label: '熱銷配方', path: '/services/hot-formulas' },
      { label: '天然原料', path: '/services/natural-ingredients' },
      { label: '各類劑型', path: '/services/dosage-forms' },
      { label: '包裝設計', path: '/services/packaging' },
    ],
  },
  {
    label: '聯絡我們',
    path: '/contact',
    sub: [
      { label: '聯絡表單', path: '/contact/form' },
      { label: '聯絡資訊', path: '/contact/info' },
    ],
  },
];

const EN_NAV: NavLink[] = [
  {
    label: 'Home',
    path: '/en',
    sub: [
      { label: 'Core Philosophy', path: '/en/home/core' },
      { label: 'Our Advantages', path: '/en/home/advantages' },
      { label: 'Experience', path: '/en/home/experience' },
      { label: 'Hot Formulas', path: '/en/home/hot-formulas' },
    ],
  },
  {
    label: 'About Kang Yong',
    path: '/en/about',
    sub: [
      { label: 'Core Advantages', path: '/en/about/core-advantages' },
      { label: 'Production', path: '/en/about/production' },
      { label: 'Success Stories', path: '/en/about/cases' },
      { label: 'Regulatory Risk', path: '/en/about/regulation' },
    ],
  },
  {
    label: 'Partnership',
    path: '/en/partnership',
    sub: [
      { label: 'Process', path: '/en/cooperation/process' },
      { label: 'Other Services', path: '/en/cooperation/services' },
      { label: 'FAQ', path: '/en/cooperation/faq' },
      { label: 'Inquiry', path: '/en/cooperation/inquiry' },
    ],
  },
  {
    label: 'OEM/ODM Services',
    path: '/en/services',
    sub: [
      { label: 'Hot Formulas', path: '/en/services/hot-formulas' },
      { label: 'Natural Ingredients', path: '/en/services/natural-ingredients' },
      { label: 'Dosage Forms', path: '/en/services/dosage-forms' },
      { label: 'Packaging', path: '/en/services/packaging' },
    ],
  },
  {
    label: 'Contact',
    path: '/en/contact',
    sub: [
      { label: 'Contact Form', path: '/en/contact/form' },
      { label: 'Contact Info', path: '/en/contact/info' },
    ],
  },
];

const JA_NAV: NavLink[] = [
  {
    label: 'ホーム',
    path: '/ja',
    sub: [
      { label: '基本理念', path: '/ja/home/core' },
      { label: '康永の強み', path: '/ja/home/advantages' },
      { label: 'サービス実績', path: '/ja/home/experience' },
      { label: '人気処方', path: '/ja/home/hot-formulas' },
    ],
  },
  {
    label: '康永について',
    path: '/ja/about',
    sub: [
      { label: 'コアアドバンテージ', path: '/ja/about/core-advantages' },
      { label: 'プロ製造', path: '/ja/about/production' },
      { label: '成功事例', path: '/ja/about/cases' },
      { label: '法規制リスク', path: '/ja/about/regulation' },
    ],
  },
  {
    label: '提携について',
    path: '/ja/partnership',
    sub: [
      { label: '協力プロセス', path: '/ja/cooperation/process' },
      { label: 'その他サービス', path: '/ja/cooperation/services' },
      { label: 'よくある質問', path: '/ja/cooperation/faq' },
      { label: 'お問い合わせ', path: '/ja/cooperation/inquiry' },
    ],
  },
  {
    label: 'OEM/ODMサービス',
    path: '/ja/services',
    sub: [
      { label: '人気処方', path: '/ja/services/hot-formulas' },
      { label: '天然原料', path: '/ja/services/natural-ingredients' },
      { label: '各種剤型', path: '/ja/services/dosage-forms' },
      { label: 'パッケージ', path: '/ja/services/packaging' },
    ],
  },
  {
    label: 'お問い合わせ',
    path: '/ja/contact',
    sub: [
      { label: 'お問い合わせフォーム', path: '/ja/contact/form' },
      { label: '連絡先情報', path: '/ja/contact/info' },
    ],
  },
];

const NAV_MAP: Record<Locale, NavLink[]> = {
  zh: ZH_NAV,
  en: EN_NAV,
  ja: JA_NAV,
};

const CTA_LABEL: Record<Locale, string> = {
  zh: '我要諮詢',
  en: 'Get Quote',
  ja: 'お問い合わせ',
};

// ── 前台隱藏的導覽子選單路徑（隱藏但不刪除，未來可恢復）────
const HIDDEN_SUB_PATHS = new Set([
  // 首頁子選單：核心理念 / 康永優勢 / 服務經歷 / 熱銷配方
  '/home/core',
  '/home/advantages',
  '/home/experience',
  '/home/hot-formulas',
  '/en/home/core',
  '/en/home/advantages',
  '/en/home/experience',
  '/en/home/hot-formulas',
  '/ja/home/core',
  '/ja/home/advantages',
  '/ja/home/experience',
  '/ja/home/hot-formulas',
  // 合作相關子選單：立即洽詢
  '/cooperation/inquiry',
  '/en/cooperation/inquiry',
  '/ja/cooperation/inquiry',
  // 聯絡我們子選單：聯絡表單 / 聯絡資訊
  '/contact/form',
  '/contact/info',
  '/en/contact/form',
  '/en/contact/info',
  '/ja/contact/form',
  '/ja/contact/info',
]);

// ── 語系選項（前台不顯示日文）────
const VISIBLE_LOCALES = [
  { code: 'zh' as Locale, label: '繁體中文' },
  { code: 'en' as Locale, label: 'English' },
];

// Pages with full-screen dark hero — navbar starts transparent
const HERO_DARK_PAGES = ['/', '/about', '/partnership', '/services', '/en', '/en/about', '/en/partnership', '/en/services', '/ja', '/ja/about', '/ja/partnership', '/ja/services'];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileItems, setExpandedMobileItems] = useState<Set<string>>(new Set());
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, switchLocale } = useLocale();

  const navLinks = NAV_MAP[locale];
  const visibleNavLinks = navLinks.map((link) => ({
    ...link,
    sub: link.sub.filter((item) => !HIDDEN_SUB_PATHS.has(item.path)),
  }));

  const isHeroDark = HERO_DARK_PAGES.includes(location.pathname);
  const isWhiteBg = isScrolled || !isHeroDark;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsScrolled(window.scrollY > 10);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setExpandedMobileItems(new Set());
  }, [location.pathname]);

  const handleMouseEnter = (path: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(path);
  };

  const handleMouseLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const handleSubItemClick = (subPath: string) => {
    setActiveDropdown(null);
    navigate(subPath);
  };

  const handleMobileSubItemClick = (subPath: string) => {
    setIsMobileMenuOpen(false);
    setExpandedMobileItems(new Set());
    navigate(subPath);
  };

  const toggleMobileItem = (path: string) => {
    setExpandedMobileItems((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/' || path === '/en' || path === '/ja') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isWhiteBg ? 'bg-white border-b border-gray-100' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to={locale === 'zh' ? '/' : `/${locale}`}
            className="flex items-center gap-2 flex-shrink-0 cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e260b0535e420cf4debd81c8e514ae3f.png"
              alt="康永生技 Logo"
              className={`h-10 w-auto object-contain transition-all duration-300 ${!isWhiteBg ? 'brightness-0 invert' : ''}`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {visibleNavLinks.map((link) => {
              const hasSub = link.sub.length > 0;
              return (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => hasSub && handleMouseEnter(link.path)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={link.path}
                  className={`relative text-[1rem] font-medium whitespace-nowrap transition-colors duration-300 pb-1 group flex items-center gap-1 ${
                    isActive(link.path)
                      ? isWhiteBg ? 'text-[#4CAF50]' : 'text-white'
                      : isWhiteBg
                      ? 'text-gray-600 hover:text-[#4CAF50]'
                      : 'text-white/85 hover:text-white'
                  }`}
                >
                  {link.label}
                  {hasSub && (
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                        activeDropdown === link.path ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                  <span
                    className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 ${
                      isWhiteBg ? 'bg-[#4CAF50]' : 'bg-white'
                    } ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}
                  />
                </Link>

                {/* Dropdown */}
                {hasSub && activeDropdown === link.path && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-xl border border-gray-100 overflow-hidden z-[200]"
                    style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.10)', minWidth: '130px' }}
                    onMouseEnter={() => handleMouseEnter(link.path)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-100 rotate-45" />
                    <ul className="py-1.5 relative">
                      {link.sub.map((item) => (
                        <li key={item.path}>
                          <button
                            onClick={() => handleSubItemClick(item.path)}
                            className="w-full text-left whitespace-nowrap text-sm text-gray-600 px-5 py-2.5 hover:bg-[#f0faf0] hover:text-[#4CAF50] transition-colors duration-150 cursor-pointer"
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
            })}
          </nav>

          {/* Right: locale switcher + CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LocaleSwitcher variant={isWhiteBg ? 'light' : 'dark'} />
            <a
              href={LINE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`whitespace-nowrap text-[0.9375rem] font-semibold px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer border ${
                isWhiteBg
                  ? 'bg-[#4CAF50] text-white border-[#4CAF50] hover:bg-[#43a047]'
                  : 'bg-white/15 text-white border-white/60 hover:bg-white/30'
              }`}
            >
              {CTA_LABEL[locale]}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="開啟選單"
          >
            <i className={`ri-menu-line text-2xl ${isWhiteBg ? 'text-gray-700' : 'text-white'}`} />
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[85vw] max-w-[360px] z-[70] bg-white flex flex-col transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <Link
            to={locale === 'zh' ? '/' : `/${locale}`}
            className="flex items-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e260b0535e420cf4debd81c8e514ae3f.png"
              alt="康永生技 Logo"
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            className="flex items-center justify-center w-9 h-9 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="關閉選單"
          >
            <i className="ri-close-line text-2xl text-gray-600" />
          </button>
        </div>

        <nav className="flex-1 flex flex-col overflow-y-auto px-4 py-2">
          {visibleNavLinks.map((link) => {
            const isExpanded = expandedMobileItems.has(link.path);
            const active = isActive(link.path);
            const hasSub = link.sub.length > 0;
            return (
              <div key={link.path} className="border-b border-gray-100">
                <div className="flex items-center">
                  <Link
                    to={link.path}
                    className={`flex-1 flex items-center text-[1.125rem] font-medium py-4 pl-2 transition-colors duration-200 cursor-pointer ${
                      active ? 'text-[#4CAF50]' : 'text-gray-700 hover:text-[#4CAF50]'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {active && (
                      <span className="w-1 h-5 bg-[#4CAF50] rounded-full mr-3 flex-shrink-0" />
                    )}
                    {link.label}
                  </Link>
                  {hasSub && (
                    <button
                      className="flex items-center justify-center w-12 h-14 cursor-pointer text-gray-400 hover:text-[#4CAF50] transition-colors duration-200 flex-shrink-0"
                      onClick={() => toggleMobileItem(link.path)}
                      aria-label={isExpanded ? '收合子選單' : '展開子選單'}
                    >
                      <i
                        className={`ri-arrow-down-s-line text-xl transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>

                {hasSub && isExpanded && (
                  <div className="bg-[#f7fdf7] border-l-[3px] border-[#4CAF50] ml-2 mb-2 rounded-r-lg overflow-hidden">
                    {link.sub.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleMobileSubItemClick(item.path)}
                        className="w-full text-left text-[1rem] text-gray-600 hover:text-[#4CAF50] hover:bg-[#eef8ee] px-5 py-3.5 transition-colors duration-150 cursor-pointer block"
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4CAF50]/50 mr-2.5 align-middle" />
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Mobile bottom: locale switcher + CTA */}
        <div className="px-6 py-5 border-t border-gray-100 space-y-3">
          {/* Language switcher row */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-global-line text-[#4CAF50] text-sm" />
            </div>
            <span className="text-gray-500 text-[0.8125rem]">語系 / Language</span>
          </div>
          {/* Mobile locale toggle: same inline pill style as desktop */}
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white">
            <div className="w-3.5 h-3.5 flex items-center justify-center ml-2.5 mr-0.5 flex-shrink-0">
              <i className="ri-global-line text-xs text-gray-400" />
            </div>
            {VISIBLE_LOCALES.map((l, idx) => (
              <div key={l.code} className="flex items-center">
                {idx > 0 && (
                  <span className="text-[0.6875rem] select-none mx-0.5 text-gray-300">|</span>
                )}
                <button
                  onClick={() => { switchLocale(l.code); setIsMobileMenuOpen(false); }}
                  aria-label={`切換至${l.label}`}
                  className={`whitespace-nowrap px-3 py-2 text-[0.8125rem] font-semibold rounded-full transition-all duration-200 cursor-pointer ${
                    locale === l.code
                      ? 'bg-[#4CAF50] text-white'
                      : 'text-gray-400 hover:text-[#4CAF50]'
                  }`}
                >
                  {l.code === 'zh' ? '中文' : 'EN'}
                </button>
              </div>
            ))}
            <span className="w-1 flex-shrink-0" />
          </div>

          <a
            href={LINE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center whitespace-nowrap bg-[#4CAF50] text-white text-[1.0625rem] font-semibold py-4 rounded-full transition-all duration-300 hover:bg-[#43a047] cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {CTA_LABEL[locale]}
          </a>
        </div>
      </div>
    </>
  );
}


